// Lightweight client-side rate limit for the share modal (defense in depth).
// Client-side only; easy to bypass. App Check can add a server-side layer later.

const STORAGE_KEY = 'usavisible:share-timestamps';
const WINDOW_MS = 60 * 60 * 1000; // 1 hour
const MAX_SHARES_PER_WINDOW = 8;
const MIN_INTERVAL_MS = 15_000; // 15s between shares

function readTimestamps(): number[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter((n): n is number => typeof n === 'number');
  } catch {
    return [];
  }
}

function writeTimestamps(ts: number[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ts.slice(-50)));
  } catch {
    /* private mode */
  }
}

export type ShareRateLimitResult =
  | { allowed: true }
  | { allowed: false; reason: string; retryAfterMs: number };

export function checkShareRateLimit(now = Date.now()): ShareRateLimitResult {
  const cutoff = now - WINDOW_MS;
  const recent = readTimestamps().filter((t) => t >= cutoff);

  if (recent.length >= MAX_SHARES_PER_WINDOW) {
    const oldest = Math.min(...recent);
    const retryAfterMs = Math.max(0, oldest + WINDOW_MS - now);
    return {
      allowed: false,
      reason: 'Too many shares in the last hour. Please try again later.',
      retryAfterMs,
    };
  }

  const last = recent.length ? Math.max(...recent) : 0;
  if (last && now - last < MIN_INTERVAL_MS) {
    return {
      allowed: false,
      reason: 'Please wait a moment before sharing again.',
      retryAfterMs: MIN_INTERVAL_MS - (now - last),
    };
  }

  return { allowed: true };
}

export function recordShareAttempt(now = Date.now()): void {
  const cutoff = now - WINDOW_MS;
  const recent = readTimestamps().filter((t) => t >= cutoff);
  recent.push(now);
  writeTimestamps(recent);
}
